/* eslint-disable no-underscore-dangle */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { headline3 } from "@shared/styles";
import { useLocalStorage } from "@shared/hookHelpers";
import Button from "../../../shared/v2/Button";
import { Plus, TriangleExclamationSolidV6, XmarkLargeSolidV6 } from "../../../shared/v2/Icomoon";
import TextButton from "../../../shared/v2/TextButton";
import PhoneView from "./PhoneView";
import { useFilterListOptions } from "../../../Settings/Users/helper/hookHelper";
import { useFeatureFlags } from "../../../reducers/layoutReducer/selectors";

const PhoneTypeOptions = [
  { label: "Home", value: "home", isTaken: false, index: null },
  { label: "Mobile", value: "mobile", isTaken: false, index: null },
  { label: "Work", value: "work", isTaken: false, index: null },
  { label: "Other", value: "other", isTaken: false, index: null },
];

function AddPhoneSection({
  phoneList,
  personId,
  required = false,
  setPersonValue,
  isDisplayByDefault = false,
  modalType,
}) {
  const [phoneOptions, setPhoneOptions] = useState([]);
  const featureFlags = useFeatureFlags();

  const [usersRemoveWarning, setUserRemoveWarning] = useLocalStorage("phoneWarningUsers", []);
  const [tempWarning, setTempWarning] = useState(true);

  useFilterListOptions(PhoneTypeOptions, setPhoneOptions, phoneList, "category");

  const onAddPhone = () => {
    const defaultPhone = {
      category: phoneOptions.find((o) => !o.isTaken)?.value,
      name: "phone",
      position: phoneList.length + 1,
      value: "",
      tempId: Date.now(),
    };
    if (phoneList.length > 0) {
      setPersonValue({ key: "phoneDetailsAttributes", value: [...phoneList, { ...defaultPhone }] });
    } else {
      setPersonValue({ key: "phoneDetailsAttributes", value: [{ ...defaultPhone }] });
    }
  };

  useEffect(() => {
    if ((required || isDisplayByDefault) && phoneList.length <= 0 && phoneOptions.length > 0) {
      onAddPhone();
    }
  }, [required, isDisplayByDefault, phoneList, phoneOptions]);

  return (
    <>
      <div className={headline3}>Phone Numbers</div>
      {modalType === "user" &&
        (personId ? !usersRemoveWarning.includes(personId) : tempWarning) &&
        !isDisplayByDefault &&
        featureFlags.agentSync2 && (
          <div className="tw-flex tw-items-center tw-gap-16px tw-py-12px tw-px-16px tw-rounded-md tw-border-solid tw-border-[#FAE4C3] tw-bg-[#faf1ec] tw-border-1px">
            <TriangleExclamationSolidV6 className="tw-text-[#E5941B]" />
            <div className="tw-flex tw-flex-col tw-gap-4px tw-text-[#AD6714]">
              <b>Trying to display your Brivity Number on your website?</b>
              <span>
                To prevent communication issues, add your Brivity Number to the <b>Website Info</b> section
                instead!
              </span>
            </div>
            <XmarkLargeSolidV6
              className="tw-text-[#E5941B] tw-cursor-pointer"
              onClick={() =>
                personId ? setUserRemoveWarning([...usersRemoveWarning, personId]) : setTempWarning(false)
              }
            />
          </div>
        )}
      {phoneList.length > 0 ? (
        <div>
          <div className="tw-flex tw-flex-col tw-gap-[24px]">
            {phoneList.map((phone, index) => (
              <PhoneView
                key={phone.id ? phone.id : phone.tempId}
                currentPhone={phone}
                index={index}
                removable={!((required || isDisplayByDefault) && phone.position === 1)}
                options={phoneOptions
                  .filter((option) => !option.isTaken || option.index === index)
                  .map((option) => ({ label: option.label, value: option.value }))}
                phoneList={phoneList}
                setPersonValue={setPersonValue}
                isDisplayByDefault={isDisplayByDefault}
                modalType={modalType}
              />
            ))}
          </div>
          <TextButton onClick={onAddPhone} className="tw-mt-[8px]" data-cy="add_phones_btn">
            <Plus size="s" /> Phone
          </TextButton>
        </div>
      ) : (
        <Button
          onClick={onAddPhone}
          className="tw-w-fit tw-flex tw-items-center"
          data-cy="add_init_phone_btn"
        >
          <Plus className="tw-mr-4px" size="s" /> Add Phone
        </Button>
      )}
    </>
  );
}

AddPhoneSection.propTypes = {
  required: PropTypes.bool,
  phoneList: PropTypes.arrayOf(PropTypes.shape({})),
  setPersonValue: PropTypes.func.isRequired,
  personId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isDisplayByDefault: PropTypes.bool,
  modalType: PropTypes.string.isRequired,
};

AddPhoneSection.defaultProps = {
  required: false,
  isDisplayByDefault: false,
  phoneList: [],
};

export default AddPhoneSection;
